/**
 * Input Sanitizer - Prevención de XSS e Inyección de Código
 * Limpia y valida inputs del usuario
 */

const Sanitizer = {
  
  /**
   * Escapa caracteres HTML para prevenir XSS
   */
  escapeHTML: (str) => {
    if (typeof str !== 'string') return str;
    
    const htmlEscapes = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '/': '&#x2F;'
    };
    
    return str.replace(/[&<>"'\/]/g, (char) => htmlEscapes[char]);
  },

  /**
   * Remueve tags HTML completamente
   */
  stripHTML: (str) => {
    if (typeof str !== 'string') return str;
    return str.replace(/<[^>]*>/g, '');
  },

  /**
   * Limpia JavaScript malicioso
   */
  removeScripts: (str) => {
    if (typeof str !== 'string') return str;
    
    return str
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  },

  /**
   * Valida y limpia URLs
   */
  sanitizeURL: (url) => {
    if (typeof url !== 'string') return '';
    
    // Solo permitir http, https, mailto
    const allowedProtocols = ['http:', 'https:', 'mailto:'];
    
    try {
      const urlObj = new URL(url);
      if (!allowedProtocols.includes(urlObj.protocol)) {
        return '';
      }
      return urlObj.href;
    } catch {
      // Si no es URL válida, escapar como texto
      return Sanitizer.escapeHTML(url);
    }
  },

  /**
   * Limpia SQL injection attempts
   */
  sanitizeSQL: (str) => {
    if (typeof str !== 'string') return str;
    
    const sqlPatterns = [
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE)\b)/gi,
      /(UNION\s+ALL|UNION\s+SELECT)/gi,
      /(--|\/\*|\*\/|;)/g,
      /(\bOR\b.*?=.*?|1=1|'=')/gi
    ];
    
    let cleaned = str;
    sqlPatterns.forEach(pattern => {
      cleaned = cleaned.replace(pattern, '');
    });
    
    return cleaned.trim();
  },

  /**
   * Valida email
   */
  sanitizeEmail: (email) => {
    if (typeof email !== 'string') return '';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    email = email.trim().toLowerCase();
    
    return emailRegex.test(email) ? email : '';
  },

  /**
   * Valida teléfono
   */
  sanitizePhone: (phone) => {
    if (typeof phone !== 'string') return '';
    
    // Remover todo excepto números, +, (, ), -, espacios
    return phone.replace(/[^\d\+\(\)\-\s]/g, '').trim();
  },

  /**
   * Limpia nombre (solo letras, espacios, acentos)
   */
  sanitizeName: (name) => {
    if (typeof name !== 'string') return '';
    
    // Permitir letras, espacios, acentos, ñ
    return name.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').trim();
  },

  /**
   * Limpia número entero
   */
  sanitizeInteger: (value) => {
    const num = parseInt(value, 10);
    return isNaN(num) ? 0 : num;
  },

  /**
   * Limpia número decimal
   */
  sanitizeFloat: (value, decimals = 2) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : parseFloat(num.toFixed(decimals));
  },

  /**
   * Sanitiza objeto completo recursivamente
   */
  sanitizeObject: (obj, schema = {}) => {
    if (typeof obj !== 'object' || obj === null) return obj;
    
    const sanitized = {};
    
    for (const [key, value] of Object.entries(obj)) {
      const sanitizeType = schema[key] || 'text';
      
      if (typeof value === 'string') {
        switch (sanitizeType) {
          case 'html':
            sanitized[key] = Sanitizer.stripHTML(value);
            break;
          case 'url':
            sanitized[key] = Sanitizer.sanitizeURL(value);
            break;
          case 'email':
            sanitized[key] = Sanitizer.sanitizeEmail(value);
            break;
          case 'phone':
            sanitized[key] = Sanitizer.sanitizePhone(value);
            break;
          case 'name':
            sanitized[key] = Sanitizer.sanitizeName(value);
            break;
          case 'sql':
            sanitized[key] = Sanitizer.sanitizeSQL(value);
            break;
          case 'number':
            sanitized[key] = Sanitizer.sanitizeInteger(value);
            break;
          default:
            sanitized[key] = Sanitizer.escapeHTML(value);
        }
      } else if (typeof value === 'object') {
        sanitized[key] = Sanitizer.sanitizeObject(value, schema[key] || {});
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  },

  /**
   * Sanitiza formulario automáticamente
   */
  sanitizeForm: (formElement, schema = {}) => {
    const formData = new FormData(formElement);
    const sanitized = {};
    
    for (const [key, value] of formData.entries()) {
      const sanitizeType = schema[key] || 'text';
      sanitized[key] = Sanitizer.sanitizeByType(value, sanitizeType);
    }
    
    return sanitized;
  },

  /**
   * Sanitiza según tipo
   */
  sanitizeByType: (value, type) => {
    switch (type) {
      case 'html': return Sanitizer.stripHTML(value);
      case 'url': return Sanitizer.sanitizeURL(value);
      case 'email': return Sanitizer.sanitizeEmail(value);
      case 'phone': return Sanitizer.sanitizePhone(value);
      case 'name': return Sanitizer.sanitizeName(value);
      case 'sql': return Sanitizer.sanitizeSQL(value);
      case 'number': return Sanitizer.sanitizeInteger(value);
      case 'float': return Sanitizer.sanitizeFloat(value);
      default: return Sanitizer.escapeHTML(value);
    }
  },

  /**
   * Valida longitud de string
   */
  validateLength: (str, min = 0, max = Infinity) => {
    if (typeof str !== 'string') return false;
    const len = str.trim().length;
    return len >= min && len <= max;
  },

  /**
   * Detecta patrones sospechosos
   */
  isSuspicious: (str) => {
    if (typeof str !== 'string') return false;
    
    const suspiciousPatterns = [
      /<script/gi,
      /javascript:/gi,
      /on\w+=/gi,
      /<iframe/gi,
      /eval\(/gi,
      /document\.write/gi,
      /window\.location/gi,
      /\bSELECT\b.*\bFROM\b/gi,
      /\bUNION\b.*\bSELECT\b/gi
    ];
    
    return suspiciousPatterns.some(pattern => pattern.test(str));
  }
};

// Exportar como módulo si es posible
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Sanitizer;
}

// Hacer disponible globalmente
if (typeof window !== 'undefined') {
  window.Sanitizer = Sanitizer;
}
